import { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    ShoppingCart,
    Plus,
    Trash2,
    ArrowLeft,
    Package,
    Calendar,
    UtensilsCrossed,
    Check,
    Search,
    BookOpen,
} from 'lucide-react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import toast from 'react-hot-toast';
import { useMyProgram } from '@/services/programs';
import { SHOP_FOR_PLAN_PATH } from '@/lib/planShopping';
import { useRecipes } from '@/services/recipes';
import { useDebounce } from '@/hooks/useDebounce';
import AddToShoppingListDialog from '@/components/AddToShoppingListDialog';
import { useAuthStore } from '@/store/useAuthStore';
import { Feature } from '@/types/auth';
import { FeatureGate } from '@/components/features/FeatureGate';
import { ShoppingListsLockedPage } from '@/components/features/FeatureLockedPage';
import { RecipeImage } from '@/components/RecipeImage';

interface Ingredient {
    id: string;
    name: string;
    categoryLegacy?: string | null;
}

// A line typed on the manual tab: a catalogue ingredient when one was picked
// (or matched by name), otherwise free text (#330: never a new Ingredient).
interface ShoppingListItem {
    ingredientId?: string;
    ingredientName: string;
    amount: number;
    unit: string;
    estimatedCost?: number;
}

const NewShoppingListContent = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const { hasFeature } = useAuthStore();

    // Old links from My Menu carried a week; the plan picker replaces that flow.
    const cameFromMenu = !!searchParams.get('startDate') && !!searchParams.get('endDate');

    const [activeTab, setActiveTab] = useState<'manual' | 'recipes'>('recipes');

    // Manual creation state
    const [listName, setListName] = useState('');
    const [items, setItems] = useState<ShoppingListItem[]>([]);
    const [newItem, setNewItem] = useState({
        ingredientName: '',
        amount: '',
        unit: 'piece',
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [pickedIngredient, setPickedIngredient] = useState<Ingredient | null>(null);

    // Recipe-based creation state
    const [recipeSearch, setRecipeSearch] = useState('');
    const [selectedRecipes, setSelectedRecipes] = useState<
        Map<string, { name: string; baseServings: number; servings: number }>
    >(new Map());
    const [showRecipeDialog, setShowRecipeDialog] = useState(false);

    // Debounce so we don't hit the API on every keystroke. Only search once the
    // user has typed at least 2 characters.
    const debouncedRecipeSearch = useDebounce(recipeSearch.trim(), 250);
    const recipeSearchActive = debouncedRecipeSearch.length >= 2;
    const { data: recipeResults, isFetching: recipesFetching } = useRecipes({
        search: recipeSearchActive ? debouncedRecipeSearch : '',
        limit: 8,
    });
    const recipeOptions = recipeSearchActive ? (recipeResults?.recipes ?? []) : [];
    // Show the dropdown while the user is typing a query (covers the gap between
    // keystroke and the debounced fetch landing).
    const showRecipeDropdown = recipeSearch.trim().length >= 2;

    const addRecipe = (r: { id: string; name: string; servings?: number }) => {
        setSelectedRecipes((prev) => {
            if (prev.has(r.id)) return prev;
            const next = new Map(prev);
            const base = r.servings || 1;
            next.set(r.id, { name: r.name, baseServings: base, servings: base });
            return next;
        });
        setRecipeSearch('');
    };

    const removeRecipe = (id: string) => {
        setSelectedRecipes((prev) => {
            const next = new Map(prev);
            next.delete(id);
            return next;
        });
    };

    const setRecipeServings = (id: string, servings: number) => {
        setSelectedRecipes((prev) => {
            const entry = prev.get(id);
            if (!entry || servings < 1) return prev;
            const next = new Map(prev);
            next.set(id, { ...entry, servings });
            return next;
        });
    };

    const recipeSelections = useMemo(
        () =>
            Array.from(selectedRecipes.entries()).map(([recipeId, v]) => ({
                recipeId,
                servings: v.servings,
            })),
        [selectedRecipes],
    );

    const { data: assignment } = useMyProgram();

    // Fetch ingredients for autocomplete
    const { data: ingredients = [] } = useQuery<Ingredient[]>({
        queryKey: ['ingredients-search', searchTerm],
        queryFn: async () => {
            if (!searchTerm || searchTerm.length < 2) return [];
            const response = await api.get(
                `/ingredients/search?name=${encodeURIComponent(searchTerm)}&limit=5`,
            );
            return response.data;
        },
        enabled: searchTerm.length >= 2,
    });

    // Create shopping list mutation
    const createMutation = useMutation({
        mutationFn: async (data: { name: string; items: ShoppingListItem[] }) => {
            // Catalogue lines go in with the list; free-text lines go through the
            // item endpoint, which files them under "Added by you" without
            // creating a catalogue ingredient.
            const formattedItems = data.items
                .filter((item) => item.ingredientId)
                .map((item) => ({
                    ingredientId: item.ingredientId,
                    amount: item.amount,
                    unit: item.unit,
                    estimatedCost: item.estimatedCost,
                }));

            const response = await api.post('/shopping-lists', {
                name: data.name,
                items: formattedItems,
            });
            const listId: string = response.data.id;
            for (const item of data.items.filter((line) => !line.ingredientId)) {
                await api.post(`/shopping-lists/${listId}/items`, {
                    name: item.ingredientName,
                    amount: item.amount,
                    unit: item.unit,
                });
            }
            return response.data;
        },
        onSuccess: (list: { id: string }) => {
            queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
            toast.success('Shopping list created successfully!');
            navigate(`/shopping-lists/${list.id}`);
        },
        onError: () => {
            toast.error('Failed to create shopping list');
        },
    });

    const handleAddItem = async () => {
        if (!newItem.ingredientName || !newItem.amount) {
            toast.error('Please fill in ingredient name and amount');
            return;
        }

        try {
            // Use a catalogue ingredient only when one was picked from the
            // suggestions or the name matches one exactly. Anything else stays
            // free text: this page no longer creates catalogue ingredients
            // (#330), since they fed nutrition scoring as category OTHER.
            const typed = newItem.ingredientName.trim();
            let ingredientId: string | undefined;
            if (pickedIngredient && pickedIngredient.name === typed) {
                ingredientId = pickedIngredient.id;
            } else {
                const searchResponse = await api.get(
                    `/ingredients/search?name=${encodeURIComponent(typed)}&limit=5`,
                );
                const matches: Ingredient[] = searchResponse.data ?? [];
                ingredientId = matches.find(
                    (match) => match.name.toLowerCase() === typed.toLowerCase(),
                )?.id;
            }

            // Add to items list
            setItems([
                ...items,
                {
                    ingredientId,
                    ingredientName: typed,
                    amount: parseFloat(newItem.amount),
                    unit: newItem.unit,
                },
            ]);

            // Reset form
            setNewItem({
                ingredientName: '',
                amount: '',
                unit: 'piece',
            });
            setSearchTerm('');
            setPickedIngredient(null);
            toast.success('Item added');
        } catch (error) {
            toast.error('Failed to add item');
        }
    };

    const handleRemoveItem = (index: number) => {
        setItems(items.filter((_, i) => i !== index));
    };

    const handleCreateList = () => {
        if (!listName.trim()) {
            toast.error('Please enter a list name');
            return;
        }

        if (items.length === 0) {
            toast.error('Please add at least one item');
            return;
        }

        createMutation.mutate({ name: listName, items });
    };

    const handleSelectIngredient = (ingredient: Ingredient) => {
        setNewItem({ ...newItem, ingredientName: ingredient.name });
        setPickedIngredient(ingredient);
        setSearchTerm('');
    };

    const hasProgram = !!assignment && hasFeature(Feature.MEAL_PLANNING);

    if (cameFromMenu) {
        return <Navigate to={SHOP_FOR_PLAN_PATH} replace />;
    }

    return (
        <div className="max-w-4xl mx-auto px-6 py-8">
            {/* Header */}
            <div className="mb-8">
                <Button
                    variant="outline"
                    onClick={() => navigate('/shopping-lists')}
                    className="mb-4"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Shopping Lists
                </Button>
                <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center">
                    <ShoppingCart className="w-10 h-10 mr-3 text-blue-600" />
                    Create Shopping List
                </h1>
                <p className="text-gray-600 text-lg">
                    Create a custom shopping list or generate from your meal plan
                </p>
            </div>

            {/* The plan has its own page now (#330); this page keeps recipes and
                hand-made lists. */}
            {hasProgram && (
                <Card className="p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">Shopping for your plan?</h3>
                        <p className="text-sm text-gray-600">
                            Pick the days and meals off your plan and we'll make the list.
                        </p>
                    </div>
                    <Button asChild className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        <Link to={SHOP_FOR_PLAN_PATH}>
                            <Calendar className="w-4 h-4 mr-2" />
                            Shop for your plan
                        </Link>
                    </Button>
                </Card>
            )}

            {/* Tab Navigation */}
            <div className="flex flex-wrap gap-2 mb-6">
                <Button
                    variant={activeTab === 'recipes' ? 'default' : 'outline'}
                    onClick={() => setActiveTab('recipes')}
                    className={activeTab === 'recipes' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                >
                    <BookOpen className="w-4 h-4 mr-2" />
                    From Recipes
                </Button>
                <Button
                    variant={activeTab === 'manual' ? 'default' : 'outline'}
                    onClick={() => setActiveTab('manual')}
                    className={activeTab === 'manual' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Manually
                </Button>
            </div>

            {/* Recipe-based Creation */}
            {activeTab === 'recipes' && (
                <Card className="p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Build from Recipes</h3>
                    <p className="text-gray-600 mb-6">
                        Search for recipes, set how many servings you want, and we'll combine all
                        the ingredients into one list. No program needed.
                    </p>

                    {/* Recipe search */}
                    <div className="relative mb-6">
                        <Label htmlFor="recipeSearch">Add a recipe</Label>
                        <div className="relative mt-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                id="recipeSearch"
                                type="text"
                                value={recipeSearch}
                                onChange={(e) => setRecipeSearch(e.target.value)}
                                placeholder="Search recipes by name…"
                                className="pl-9"
                            />
                        </div>
                        {/* Autocomplete dropdown */}
                        {showRecipeDropdown && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                                {recipesFetching && recipeOptions.length === 0 ? (
                                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                        Searching…
                                    </div>
                                ) : recipeOptions.length === 0 ? (
                                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                        No recipes match "{debouncedRecipeSearch}"
                                    </div>
                                ) : (
                                    recipeOptions.map((recipe) => {
                                        const alreadyAdded = selectedRecipes.has(recipe.id);
                                        return (
                                            <button
                                                key={recipe.id}
                                                type="button"
                                                disabled={alreadyAdded}
                                                onClick={() => addRecipe(recipe)}
                                                className="w-full text-left px-4 py-2.5 hover:bg-emerald-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
                                            >
                                                {recipe.imageUrl ? (
                                                    <RecipeImage
                                                        src={recipe.imageUrl}
                                                        alt=""
                                                        variant="thumb"
                                                        thumbSize={40}
                                                        className="w-10 h-10 rounded-md object-cover shrink-0"
                                                    />
                                                ) : (
                                                    <div className="w-10 h-10 rounded-md bg-emerald-50 flex items-center justify-center shrink-0">
                                                        <UtensilsCrossed className="w-4 h-4 text-emerald-600" />
                                                    </div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium truncate">
                                                        {recipe.name}
                                                    </div>
                                                    {recipe.servings ? (
                                                        <div className="text-xs text-gray-500">
                                                            Serves {recipe.servings}
                                                        </div>
                                                    ) : null}
                                                </div>
                                                {alreadyAdded ? (
                                                    <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                                                ) : (
                                                    <Plus className="w-4 h-4 text-gray-400 shrink-0" />
                                                )}
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </div>

                    {/* Selected recipes */}
                    {selectedRecipes.size > 0 ? (
                        <div className="space-y-2 mb-6">
                            <h4 className="font-semibold text-gray-900">
                                Selected ({selectedRecipes.size})
                            </h4>
                            {Array.from(selectedRecipes.entries()).map(([id, entry]) => (
                                <div
                                    key={id}
                                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                                >
                                    <UtensilsCrossed className="w-4 h-4 text-emerald-600 shrink-0" />
                                    <span className="flex-1 min-w-0 font-medium truncate">
                                        {entry.name}
                                    </span>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        <input
                                            type="number"
                                            min="1"
                                            max="50"
                                            value={entry.servings}
                                            onChange={(e) =>
                                                setRecipeServings(id, parseInt(e.target.value, 10))
                                            }
                                            className="w-16 px-2 py-1 text-sm text-center border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                                        />
                                        <span className="text-xs text-gray-600">srv</span>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => removeRecipe(id)}
                                        className="text-red-600 hover:text-red-700 shrink-0"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500 mb-6">
                            <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p>Search above to add recipes to your list</p>
                        </div>
                    )}

                    <Button
                        onClick={() => setShowRecipeDialog(true)}
                        disabled={selectedRecipes.size === 0}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
                    >
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        {selectedRecipes.size === 0
                            ? 'Add recipes to continue'
                            : `Add to Shopping List (${selectedRecipes.size} recipe${selectedRecipes.size !== 1 ? 's' : ''})`}
                    </Button>
                </Card>
            )}

            {/* Manual Creation Form */}
            {activeTab === 'manual' && (
                <Card className="p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Manually</h3>

                    {/* List Name */}
                    <div className="mb-6">
                        <Label htmlFor="listName">List Name *</Label>
                        <Input
                            id="listName"
                            type="text"
                            value={listName}
                            onChange={(e) => setListName(e.target.value)}
                            placeholder="e.g., Weekly Groceries, Party Shopping"
                            className="mt-1"
                        />
                    </div>

                    {/* Add Items */}
                    <div className="border-t pt-6">
                        <h4 className="font-semibold text-gray-900 mb-4">Add Items</h4>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                            <div className="md:col-span-2 relative">
                                <Label htmlFor="ingredientName">Ingredient *</Label>
                                <Input
                                    id="ingredientName"
                                    type="text"
                                    value={newItem.ingredientName}
                                    onChange={(e) => {
                                        setNewItem({ ...newItem, ingredientName: e.target.value });
                                        setSearchTerm(e.target.value);
                                    }}
                                    placeholder="e.g., Tomatoes"
                                    className="mt-1"
                                />
                                {/* Autocomplete dropdown */}
                                {ingredients.length > 0 && searchTerm && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                        {ingredients.map((ingredient) => (
                                            <button
                                                key={ingredient.id}
                                                type="button"
                                                onClick={() => handleSelectIngredient(ingredient)}
                                                className="w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors"
                                            >
                                                <div className="font-medium">{ingredient.name}</div>
                                                <div className="text-xs text-gray-500">
                                                    {ingredient.categoryLegacy}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div>
                                <Label htmlFor="amount">Amount *</Label>
                                <Input
                                    id="amount"
                                    type="number"
                                    step="0.01"
                                    value={newItem.amount}
                                    onChange={(e) =>
                                        setNewItem({ ...newItem, amount: e.target.value })
                                    }
                                    placeholder="2"
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label htmlFor="unit">Unit</Label>
                                <select
                                    id="unit"
                                    value={newItem.unit}
                                    onChange={(e) =>
                                        setNewItem({ ...newItem, unit: e.target.value })
                                    }
                                    className="w-full px-3 py-2 mt-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="piece">Piece</option>
                                    <option value="cup">Cup</option>
                                    <option value="oz">Ounce</option>
                                    <option value="lb">Pound</option>
                                    <option value="g">Gram</option>
                                    <option value="kg">Kilogram</option>
                                    <option value="ml">Milliliter</option>
                                    <option value="l">Liter</option>
                                    <option value="tbsp">Tablespoon</option>
                                    <option value="tsp">Teaspoon</option>
                                </select>
                            </div>
                        </div>
                        <Button
                            onClick={handleAddItem}
                            variant="outline"
                            className="w-full md:w-auto"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Item
                        </Button>
                    </div>

                    {/* Items List */}
                    {items.length > 0 && (
                        <div className="border-t mt-6 pt-6">
                            <h4 className="font-semibold text-gray-900 mb-4">
                                Items ({items.length})
                            </h4>
                            <div className="space-y-2">
                                {items.map((item, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                                    >
                                        <div className="flex items-center space-x-3">
                                            <Package className="w-4 h-4 text-gray-500" />
                                            <div>
                                                <div className="font-medium">
                                                    {item.ingredientName}
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    {item.amount} {item.unit}
                                                </div>
                                            </div>
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleRemoveItem(index)}
                                            className="text-red-600 hover:text-red-700"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Card>
            )}

            {/* Action Buttons - only show for manual tab */}
            {activeTab === 'manual' && (
                <div className="flex items-center justify-between">
                    <Button variant="outline" onClick={() => navigate('/shopping-lists')}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateList}
                        disabled={createMutation.isPending || !listName || items.length === 0}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        {createMutation.isPending ? 'Creating...' : 'Create Shopping List'}
                    </Button>
                </div>
            )}

            <AddToShoppingListDialog
                open={showRecipeDialog}
                onClose={() => setShowRecipeDialog(false)}
                recipes={recipeSelections}
                onAdded={() => navigate('/shopping-lists')}
            />
        </div>
    );
};

const NewShoppingList = () => {
    return (
        <FeatureGate feature={Feature.SHOPPING_LISTS} fallback={<ShoppingListsLockedPage />}>
            <NewShoppingListContent />
        </FeatureGate>
    );
};

export default NewShoppingList;
